import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, coerceBooleanFromString } from '../schemas';
import { userSchema } from '../../interfaces/IUser';
import { userPermissionsSchema } from '../../interfaces/IUserPermissions';
import { RoleType } from '../../interfaces/RoleType.enum';
import { Languages } from '../../constants/languages';

// ============================================================
// Query/Body/Params Schemas
// ============================================================

/**
 * D3 — the projects a user is created into are a REQUIRED, top-level
 * `?projectIds=a,b`, the plural cross-project shape, because the backoffice
 * creates a user into several projects at once and no single `:projectId` path
 * segment can say that.
 *
 * They used to travel in the body, where `userSchema`'s
 * `z.array(z.string()).default([])` made them `required: false` and therefore
 * invisible to the audit. The route was NOT unguarded at runtime —
 * `collectRequestProjectIds` runs at `preValidation`, before Zod, and reads the
 * raw `body.projectIds` — so this makes the declaration say what the runtime
 * already did. The guard reads both shapes, which is what makes the move safe;
 * dropping the field from BOTH would silently un-guard user creation.
 */
const registerQuerySchema = z.object({
	shouldSendEmailInvite: coerceBooleanFromString.optional(),
	projectIds: requiredStringSchema.describe('Comma-separated projects to create the user into'),
});

/**
 * `projectIds` is omitted rather than ignored: the only place that decides which
 * projects a user is created into is the query the guard validated.
 * `organizationIds` STAYS in the body — it is not a project claim, and
 * `assertOrganizationMembership` reads top-level `body.organizationIds`.
 */
const registerBodySchema = userSchema.omit({ projectIds: true }).extend({
	language: z.enum(Languages).optional(),
});

const executiveUsersQuerySchema = z.object({
	organizationId: requiredStringSchema,
});

/**
 * The user as the API returns it.
 *
 * `password` is a bcrypt hash on the stored document and was in every one of
 * these responses, including the project roster that any member of a project can
 * read. Nothing in any client or any backend service has ever read it. Response
 * schemas drive serialization, so omitting it here actually removes it from the
 * wire rather than merely documenting that it should not be there.
 */
const userResponseSchema = userSchema.omit({ password: true });

/**
 * D1 — the assignee-rendering projection.
 *
 * Deliberately the smallest thing that can render a name: no email, no phone, no
 * role, no project or organization membership. `IUser` carries no avatar field,
 * so there is nothing else to include. This is the shape an ORDINARY user may
 * read about other people; the full record is administered-user scope.
 */
export const userDisplayNameSchema = z.object({
	username: z.string(),
	name: z.string(),
});

export type IUserDisplayName = z.infer<typeof userDisplayNameSchema>;

const MAX_DISPLAY_NAME_LOOKUPS = 200;

/**
 * A COMMA-SEPARATED list, per D1's `?usernames=a,b`. Validated as a CSV here and
 * split in the route, matching `activitiesGroup`'s `groupStatus` — the contract
 * and the handler must agree about the shape, because typing a CSV as an array
 * 400s every real call before the handler runs.
 */
const displayNamesQuerySchema = z.object({
	usernames: z
		.string()
		.refine((value) => value.split(',').filter(Boolean).length > 0, {
			message: 'usernames must be a non-empty comma-separated list',
		})
		.refine((value) => value.split(',').filter(Boolean).length <= MAX_DISPLAY_NAME_LOOKUPS, {
			message: `usernames may name at most ${MAX_DISPLAY_NAME_LOOKUPS} users`,
		})
		.describe('Comma-separated usernames to resolve, e.g. ?usernames=a,b'),
});

const resetPasswordViaMailBodySchema = z.object({
	email: requiredStringSchema,
});

/**
 * Roles whose reach ends at the project the assigning admin already administers.
 * TRUSSTOR_ADMIN bypasses the project-membership guard entirely and EXECUTIVE is granted
 * every project of its organizations, so neither is within a PROJECT_ADMIN's authority to
 * hand out — both are assignable only through `updateUserPrivileges`.
 */
export const projectScopedRoleTypes = [RoleType.PROJECT_ADMIN, RoleType.MANAGER, RoleType.VIEWER] as const;

const projectScopedPermissionsSchema = userPermissionsSchema.extend({
	roleType: z.enum(projectScopedRoleTypes),
});

/** Profile fields, editable by any admin of a project the user belongs to. */
const editableUserFieldsSchema = userSchema
	.pick({ name: true })
	.partial()
	.extend({
		// The user schema defaults `phoneNumber` to null. On a partial edit that default would
		// clear a stored number every time the key is absent, so it is re-declared as plainly
		// optional — absent must mean "leave alone", never "set to null".
		phoneNumber: z.string().nullable().optional(),
		language: z.enum(Languages).optional(),
	});

/**
 * The ordinary user edit.
 *
 * `projectIds`, `organizationIds` and `isActive` decide which projects a user reaches and
 * whether the account works at all, and a TRUSSTOR_ADMIN `roleType` bypasses project
 * scoping altogether — all of them belong to `updateUserPrivileges`. `password` is never
 * accepted here: a password is only ever set by redeeming a set-password token.
 * `username` and `email` are identity and are fixed at registration — `email` also
 * addresses the unauthenticated reset-password mail, so it must not be reassignable.
 *
 * Strict: a privileged field is rejected outright rather than silently dropped, so a
 * caller can never believe a membership change was applied when it was not.
 */
const updateUserBodySchema = editableUserFieldsSchema
	.extend({
		permissions: projectScopedPermissionsSchema.optional(),
	})
	.strict();

/**
 * The privileged user edit — any role, project and organization membership, and account
 * state, on top of everything the ordinary edit accepts. TRUSSTOR_ADMIN only. `password`
 * is excluded here too; the set-password token flow is the only way to set one.
 */
const updateUserPrivilegesBodySchema = editableUserFieldsSchema
	.extend({
		permissions: userPermissionsSchema.optional(),
		projectIds: z.array(z.string()).optional(),
		organizationIds: z.array(z.string()).optional(),
		isActive: z.boolean().optional(),
	})
	.strict();

export type IUpdateUserBody = z.infer<typeof updateUserBodySchema>;
export type IProjectScopedUserPermissions = z.infer<typeof projectScopedPermissionsSchema>;

const usernameParamsSchema = z.object({
	username: requiredStringSchema,
});

const userIdParamsSchema = z.object({
	userId: requiredStringSchema,
});

const projectIdParamsSchema = z.object({
	projectId: requiredStringSchema,
});

const projectUserParamsSchema = z.object({
	projectId: requiredStringSchema,
	userId: requiredStringSchema,
});

const getUsersByProjectQuerySchema = z.object({
	withoutAdmin: coerceBooleanFromString.optional(),
});

const removeUsersFromProjectBodySchema = z.object({
	ids: z.array(z.string()).min(1),
});

// ============================================================
// Contract
// ============================================================

/**
 * User contract.
 *
 * `{ root: true }` because the project-keyed routes have to read
 * `/projects/:projectId/users`, and the project segment cannot sit behind a
 * contract-name prefix. The consequence is that EVERY route in this file now
 * spells its own full path — nothing is derived from the contract name any more,
 * so a route left unspelled would silently move to the service root. The whole
 * table is pinned literally, present AND absent, in
 * `Entities/src/tests/unit/fastify/userRouteTable.test.ts`.
 *
 * The collection segment is `/users`, plural, per the API standards. TWO routes
 * deliberately keep the old singular `/user/` segment:
 *
 *   PUT  /api/entities/user/password
 *   POST /api/entities/user/resetPasswordViaMail
 *
 * They are the unauthenticated identity flows. d-orca's `auth.client.ts` calls
 * both by hand-built string, and `fastify/app.ts`'s `skipRoutes` matches them by
 * substring — renaming either would turn the public password-set flow into a 401
 * for the web app AND for d-orca, with nothing in either repository to catch it.
 * The inconsistency is the price of that, and it is deliberate.
 *
 * IMPORTANT: Specific paths must come BEFORE parameterized paths.
 */
export const userContract = defineContract(
	'user',
	{
		registerUser: route({
			method: 'POST',
			path: '/users',
			query: registerQuerySchema,
			body: registerBodySchema,
			response: z.union([z.string(), userResponseSchema]),
			operationId: 'registerUser',
			tags: ['User'],
			summary: 'Create a user in one or more projects',
			description:
				'`?projectIds=a,b` is required and is the only source of the projects the user is created into; it is deliberately plural, because the backoffice creates a user into several at once.',
		}),
		/**
		 * D4 — the executive cascade. Adding a project to an organization's
		 * EXECUTIVE users touches two tenancy dimensions at once, and both are now
		 * declared where the corresponding central guard can read them: the project
		 * as a path param for `assertProjectMembership`, the organization as a
		 * top-level query param for `assertOrganizationMembership`. It carried both
		 * in the body before, where only the project guard could see one of them.
		 *
		 * POST rather than PUT because it appends a membership to a set of users
		 * rather than replacing anything.
		 */
		addProjectIdToExecutiveUsers: route({
			method: 'POST',
			path: '/projects/:projectId/executiveUsers',
			params: projectIdParamsSchema,
			query: executiveUsersQuerySchema,
			response: z.string(),
			operationId: 'addProjectIdToExecutiveUsers',
			tags: ['User'],
			summary: "Grant a project to an organization's executive users",
		}),
		/**
		 * ADMINISTERED-USER scope. The target moves out of the body and into the
		 * path, which is the shape `docs/authorization-scopes.md` gives this scope —
		 * and the reason it can be guarded at all: the handler loads the named user
		 * and `assertAdministersUser` compares the LOADED row's organizations
		 * against the caller's. Role alone let an Admin of one organization invite
		 * into another.
		 */
		inviteViaMail: route({
			method: 'POST',
			path: '/users/:username/invitations',
			params: usernameParamsSchema,
			response: z.string(),
			operationId: 'inviteViaMail',
			tags: ['User'],
			summary: 'Send an invitation mail to an administered user',
		}),
		resetPasswordViaMail: route({
			method: 'POST',
			path: '/user/resetPasswordViaMail',
			body: resetPasswordViaMailBodySchema,
			response: z.string(),
			operationId: 'resetPasswordViaMail',
			tags: ['User'],
			summary: 'Send reset password email',
		}),
		/**
		 * GLOBAL ADMIN. Every user in the installation, with no tenant dimension of
		 * any kind — so it is TRUSSTOR_ADMIN only. It was `allowAdminRoles`, which
		 * meant a PROJECT_ADMIN of one project could read the entire user table of
		 * every tenant, and it reached that table through `getUsersByFilter({})`.
		 */
		getAllProjectsUsers: route({
			method: 'GET',
			path: '/users',
			response: z.array(userResponseSchema),
			operationId: 'getAllProjectsUsers',
			tags: ['User'],
			summary: 'Get every user in the installation',
		}),
		/**
		 * D1 — ADMINISTERED-USER scope, and it was previously ungated entirely: any
		 * authenticated user could read any user's full record, email and phone
		 * included. It is now `allowAdminRoles` plus `assertAdministersUser` on the
		 * loaded target.
		 *
		 * Ordinary users who need to render somebody's name use
		 * `GET /users/displayNames` instead, which returns username and name only.
		 */
		getUserByUsername: route({
			method: 'GET',
			path: '/users/:username',
			params: usernameParamsSchema,
			response: userResponseSchema.nullable(),
			operationId: 'getUserByUsername',
			tags: ['User'],
			summary: 'Get an administered user by username',
		}),
		/**
		 * D1's ordinary-user read. Any authenticated caller, ORGANIZATION-scoped:
		 * the result contains only users who share at least one organization with
		 * the caller, so it cannot be used to enumerate the installation.
		 * TRUSSTOR_ADMIN sees every match.
		 *
		 * A username that does not exist, and one belonging to somebody outside the
		 * caller's organizations, are both simply ABSENT from the response — never a
		 * 403 and never a 404, so the endpoint cannot be used to test whether a
		 * username exists.
		 */
		getUserDisplayNames: route({
			method: 'GET',
			path: '/users/displayNames',
			query: displayNamesQuerySchema,
			response: z.array(userDisplayNameSchema),
			operationId: 'getUserDisplayNames',
			tags: ['User'],
			summary: "Resolve usernames to display names, within the caller's organizations",
		}),
		getUsersByProject: route({
			method: 'GET',
			path: '/projects/:projectId/users',
			params: projectIdParamsSchema,
			query: getUsersByProjectQuerySchema,
			response: z.array(userResponseSchema),
			operationId: 'getUsersByProject',
			tags: ['User'],
			summary: 'Get the users of a project',
			description:
				'The project roster. `withoutAdmin` drops Trusstor superusers from the list; it is a display filter, not a scope.',
		}),
		/**
		 * ADMINISTERED-USER scope, like `getUserByUsername`, and previously ungated
		 * in the same way.
		 *
		 * It survives — rather than being deleted with the other zero-caller
		 * routes — because it HAS a caller that no grep for the operation name could
		 * find: `trusstorutils/vars/trusstorutils.groovy:353` reads a user's
		 * `projectIds` by `_id` with a hand-built curl. The `id` -> `userId` rename
		 * and the `/id/` segment are what keep it from colliding with
		 * `GET /users/:username` now that that route lost its `/username/` prefix.
		 */
		getUserById: route({
			method: 'GET',
			path: '/users/id/:userId',
			params: userIdParamsSchema,
			response: userResponseSchema,
			operationId: 'getUserById',
			tags: ['User'],
			summary: 'Get an administered user by id',
		}),
		updateUserPrivileges: route({
			method: 'PUT',
			path: '/users/:username/privileges',
			params: usernameParamsSchema,
			body: updateUserPrivilegesBodySchema,
			response: userResponseSchema,
			operationId: 'updateUserPrivileges',
			tags: ['User'],
			summary: "Update a user's role, project and organization membership, and account state",
		}),
		updateUser: route({
			method: 'PUT',
			path: '/users/:username',
			params: usernameParamsSchema,
			body: updateUserBodySchema,
			response: userResponseSchema,
			operationId: 'updateUser',
			tags: ['User'],
			summary: 'Update user by username',
		}),
		/**
		 * The one route on which a user ACCOUNT can cease to exist, and only as a
		 * consequence: a user whose last remaining project is this one is deleted
		 * outright; a user who belongs to others merely loses this membership.
		 * A userId outside this project is a 404, never a delete.
		 */
		deleteUser: route({
			method: 'DELETE',
			path: '/projects/:projectId/users/:userId',
			params: projectUserParamsSchema,
			response: z.string(),
			operationId: 'deleteUser',
			tags: ['User'],
			summary: 'Remove a user from a project, deleting the account if it was their last',
			description:
				'Removes the project from the user. If it was the only project the user belonged to, the account is deleted. A userId that belongs to a different project returns 404 — it is never reachable from here.',
		}),
		/**
		 * MEMBERSHIP REMOVAL ONLY — never a deletion, not even for a user whose
		 * last project this is. It was named `deleteUsersByUsernameIds` and
		 * hard-deleted every matched user regardless of their other projects, so
		 * the two delete routes disagreed and this one lost data. Full account
		 * deletion lives on the single-user route above.
		 */
		removeUsersFromProject: route({
			method: 'DELETE',
			path: '/projects/:projectId/users',
			params: projectIdParamsSchema,
			body: removeUsersFromProjectBodySchema,
			response: z.string(),
			operationId: 'removeUsersFromProject',
			tags: ['User'],
			summary: 'Remove several users from a project',
			description:
				'Removes the project from each named user; their accounts and their other projects are untouched, even when this was their last project. Ids naming a user outside this project match nothing and are skipped. Full account deletion is only available on DELETE /projects/:projectId/users/:userId.',
		}),
	},
	{ root: true }
);
